import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import DropdownTreeSelect from 'react-dropdown-tree-select';
import 'react-dropdown-tree-select/dist/styles.css';
import { TabulatorFull as Tabulator } from "tabulator-tables";
import { Button, Col, FormFeedback, FormGroup, Input, Label, Row, Table } from 'reactstrap';
import SelectNewPartModal from '../../components/layout/Modal/SelectNewPartModal';
import SelectNewReferenceTypeModal from '../../components/layout/Modal/SelectNewReferenceTypeModal';
import { useAppDispatch, useAppSelector } from '../../hooks/index';
import { Concept } from '../../redux/ConceptSlice';
import { Edit, isAdmin } from '../../redux/MiscSlice';
import {
    addReferenceAssignmentChange, addReferenceChange, clearEditReference, getLatestReferenceVersion,
    getPartDefinitionChanges, getTaxonomyChanges, haveChangesForReference, includeChangedReferencesForConcept,
    ReferenceAssignmentChange, selectEditReference, setEditReference, TaxonomyChangesState
} from '../../redux/TaxonomyChangesSlice';
import { DtsConcept, getSelectedTaxonomyConcept, resetTaxonomyConceptState } from '../../redux/TaxonomyConceptSlice';
import {
    clearSelectedReference, clearSelectedReferenceDirty, getDataTypeForDefinition, getPartValue,
    getRefMap, getUasId, loadAllReferenceAssignments, loadAllTaxonomyReferences, Part, PartDefinition,
    PartValue, Reference, ReferenceAssignmentsRequest, selectAllPartDefinitions, selectAllReferences,
    selectCurrentReference, selectReferenceAssignments, setSelectedReference, updateSelectedReferencePart, clearSelectedReferenceAssignments,
    referenceHasPart, isShareable
} from '../../redux/TaxonomyReferenceSlice';
import { ActiveTaxonomyVersionInfo, getReferenceForVersion, selectCurrentVersion, setIsTaxonomyDirty, TaxonomyTypeEnum, VersionRef } from '../../redux/TaxonomyVersionSlice';
import { ErrorGrid } from '../../redux/ValidationSlice';
import enums from '../../utils/enums';
import utilityHelper from '../../utils/utilityHelper';
import { StyledSelectableListItem } from '../core/ConceptManagementCore';
import WYSIWYGEditor from './WYSIWYGEditor';

interface RefTableData {
    part: string;
    value: string
    datatype: string
}

const ConceptReferenceEditor = (props: { currentVersionedConcept : DtsConcept | null }) => {
    const dispatch = useAppDispatch();
    const isAdminUser: boolean = useAppSelector(isAdmin);
    var versionedConcept: DtsConcept | null = props.currentVersionedConcept; //useAppSelector(getSelectedTaxonomyConcept);
    var currentConcept: Concept | null = versionedConcept ? versionedConcept.concept : null;
    var references: Array<Reference> = useAppSelector(selectAllReferences);
    var selectedReference: Reference | null = useAppSelector(selectCurrentReference);
    var currentEditReference: Reference | null = useAppSelector(selectEditReference);
    var partDefinitions: PartDefinition[] = useAppSelector(selectAllPartDefinitions);
    var refConcepts: Concept[] = useAppSelector(selectReferenceAssignments);
    var changes: TaxonomyChangesState | null = useAppSelector(getTaxonomyChanges);
    var [currentRef, setCurrentRef] = useState<Reference | null>(selectedReference);
    var [whereUsed, setWhereUsed] = useState<Concept[]>(refConcepts);
    var [validationErrorDetails, setValidationErrorDetails] = useState("");
    var [validationError, setValidationError] = useState("");
    var versionInfo: ActiveTaxonomyVersionInfo | null = useAppSelector(selectCurrentVersion);
    const [refArticle, setRefArticle] = useState<string>("");
    const [versionType, setVersionType] = useState<TaxonomyTypeEnum>(getReferenceForVersion(versionInfo, -1).type);
    const [versionYear, setVersionYear] = useState(versionInfo ? versionInfo.year : 2022);
    const [modalPartIsOpen, setModalPartIsOpen] = useState(false);
    const [modalReferenceIsOpen, setModalReferenceIsOpen] = useState(false);
    const [tablePartMap, setTablePartMap] = useState<any>({});
    const isDoneRef = useRef(false);
    var tabulatorRef = React.useRef<any>(null);


    const changedPartDefinitions = useAppSelector(getPartDefinitionChanges);

    const collectAllPartDefinitions = function (): PartDefinition[] {
        var allDefs: PartDefinition[] = [];
        if (partDefinitions) {
            allDefs = allDefs.concat(partDefinitions);
        }
        if (changedPartDefinitions) {
            allDefs = allDefs.concat(changedPartDefinitions);
        }
        return allDefs;
    }
    const [allPartDefinitions, setAllPartDefinitions] = useState<PartDefinition[]>(collectAllPartDefinitions());

    const getAssignedReferences = function(vo: DtsConcept | null) {
        var assignedRefs : Array<Reference> = [];
        if (vo && vo.concept && changes) {
            assignedRefs = includeChangedReferencesForConcept(vo.concept, changes);
        }
        return assignedRefs;
    }
    const [currentRefAssignments, setCurrentRefAssignments] = useState(getAssignedReferences(versionedConcept));

    const isNewReference = function (r: Reference) {
        if (!r.hasOwnProperty("new")) {
            return false;
        }
        return r.new === true;
    }

    const isDirtyReference = function (r: Reference) {
        if (!r.hasOwnProperty("dirty")) {
            return false;
        }
        return r.dirty === true;
    }

   const isNewSharedReference = function (r: Reference) {
        if (r.type === enums.referenceTypes.standard && isNewReference(r)) {
            return true;
        }
        return false;
    }

    const getReferenceStatusIconClass = function (reference: Reference | null) {
        if (reference) {
            if (reference.hasOwnProperty("new") && reference.new === true) {
                return "bi bi-file-plus-fill";
            }
//            if (isDirtyReference(reference)) {
//                return "bi bi-pencil-fill";
//            }
            if (changes && haveChangesForReference(reference, changes)) {
                return "bi bi-hexagon-half";
            }
        }
        return "";
    }
    const getReferenceEditStatusIconClass = function (reference: Reference | null) {
        if (reference) {
            if (isDirtyReference(reference)) {
                return "bi bi-pencil-fill";
            }
        }
        return "";
    }
    const [referenceStatusIcon, setReferenceStatusIcon] = useState(getReferenceStatusIconClass(selectedReference))
    const [referenceEditStatusIcon, setReferenceEditStatusIcon] = useState(getReferenceEditStatusIconClass(selectedReference))

    const hasAssignedRef = function(curRef: Reference) {
        if (!curRef) {
            return false;
        }
        var uasId: string = getUasId(curRef, null);
        if (!versionedConcept) {
            return false;
        }
        for (var nextRef of getAssignedReferences(versionedConcept)) {
            if (getUasId(nextRef, null) === uasId) {
                // we already have this reference
                return true;
            }
        }
        return false;
    }
    const canBeAssigned = function () {
        if (!selectedReference) {
            return false;
        }
        return !hasAssignedRef(selectedReference);
    }

    const canBeUnAssigned = function () {
        if (!selectedReference) {
            return false;
        }
        return hasAssignedRef(selectedReference);
    }
    const [refIsAssignable, setRefIsAssignable] = useState(canBeAssigned());
    const [refIsUnAssignable, setRefIsUnAssignable] = useState(canBeUnAssigned());

    const setTaxonomyDirty = function () {
        var estate: Edit = {
            IsDirty: true,
            Page: ""
        }
        dispatch(setIsTaxonomyDirty(estate));
    }

    const getPartDefinition = function(partName : string): PartDefinition {
        var result: any = { group: "not found", name: partName, documentation: "", datatype: "", deprecationDate: "" };
        if( partDefinitions ) {
            var matches = partDefinitions.filter(pd => pd.name === partName);
            if( matches.length > 0 ) {
                result = matches[0];
            }
        }
        return result;
    }

    const notTablePart: any = {
        "UASID": true,
        "IssueDate": true,
        "Name": true,
        "Article": true
    } 

    const getNonRequiredParts = function( reference : Reference | null ) : Part[] {
        if( reference == null ) {
            return [];
        }
        return reference.parts.filter(origPart => !notTablePart.hasOwnProperty(origPart.definition.name) );
    }
    const updateNonRequiredParts = function( reference : Reference | null ) : any {
        var result: any = {};
        var nonreqParts: Part[] = getNonRequiredParts(reference);
        for (var p of nonreqParts) {
            if (p.value.length > 0) {
                result[p.definition.name] = p.value;
            }
        }
        return result;
    }
    const updateTablePartMap = function (reference: Reference | null) {
        var partMap: any = {

        }
        if (reference != null) {
            var partDefs = collectAllPartDefinitions();
            for (var p of getNonRequiredParts(reference)) {
                var pd: PartDefinition | null | undefined = partDefs.find(x => x.name === p.definition.name);
                if (!pd) {
                    console.log("missing def", p.definition.name);
                    continue;
                }
                partMap[p.definition.name] = p;
            }
        }
        setTablePartMap(partMap);
    }
    const getTableData = function (reference : Reference | null): RefTableData[] {
        // Test for null and undefined
        if (reference == null) {
            return [];
        }
        var rowMap: any = {

        };
        var tdata: RefTableData[] = [];
        var partDefs = collectAllPartDefinitions();
        for (var p of getNonRequiredParts( reference ) ) {
            var pd: PartDefinition | null | undefined = partDefs.find(x => x.name === p.definition.name);
            if (!pd) {
                console.log("missing def", p.definition.name);
                continue;
            }
            var rTableEntry: RefTableData = {
                part: p.definition.name,
                value: p.value,
                datatype: getDataTypeForDefinition(pd)
            }
            switch (p.definition.name) {
                case 'UASID':
                    if (isNewSharedReference(reference)) {
                        rowMap[" 1UASID"] = rTableEntry;
                    }
                    continue;

                case 'IssueDate':
                    rowMap[" 2IssueDate"] = rTableEntry;
                    break;

                case 'Name':
                    rowMap[" 3Name"] = rTableEntry;
                    break;

                case 'Chapter':
                    rowMap[" 4Chapter"] = rTableEntry;
                    break;

                default:
                    rowMap[p.definition.name] = rTableEntry;
                    break;
            }
        }
        for (var pkey of Object.keys(rowMap).sort()) {
            tdata.push(rowMap[pkey]);
        }
        return tdata;
    }

    const [tableData, setTableData] = useState<RefTableData[]>(getTableData(selectedReference));

    const switchReference = function (r: Reference) {
        var latestRef = r;
        if (changes) {
            latestRef = getLatestReferenceVersion(r, changes);   
        }
        setCurrentRef(latestRef);
        dispatch(setSelectedReference(latestRef));
        dispatch(clearEditReference);
    }

    const switchToLastSavedReference = function (r: Reference | null) {
        var latestRef : Reference | null | undefined = null;
        if (r) {
            var uasid: string = getUasId(r, null);
            if (changes && haveChangesForReference(r, changes)) {
                latestRef = getLatestReferenceVersion(r, changes);
            } else {
                latestRef = getAvailableReferencesIncludingChanges([], {}).find(existing => existing.type === r.type && getUasId(existing, null) === uasid);
            }
        }
        if (latestRef) {
            setCurrentRef(latestRef);
            setReferenceEditable(canEditCurrentReference(latestRef));
            setReferenceDirty(isReferenceDirty(latestRef));
            dispatch(setSelectedReference(latestRef));
            setRefId(getUasId(latestRef, null));
            setRefDate(getFormattedPartValue(latestRef, "IssueDate"));
            setRefName(getFormattedPartValue(latestRef, "Name"));
            if (referenceHasPart(latestRef, "Article")) {
                setRefArticle(getFormattedPartValue(latestRef, "Article"))
            }
            setReferenceEditStatusIcon("");
        } else {
            setCurrentRef(null);
            dispatch(clearSelectedReference());
        }
        dispatch(clearEditReference);
    }

    const canEditCurrentReference = function(currentRef : Reference | null) {
        if (!currentRef) {
            return false;
        }
        switch (currentRef.type) {
            case enums.referenceTypes.courtrelated:
                return false;

            default:
                return true;
        }
    }
    const isReferenceDirty = function (selref : Reference | null) {
        if (!selref || !selref.dirty) {
            return false;
        }
        return selref.dirty === true;
    }
    const [referenceEditable, setReferenceEditable] = useState(canEditCurrentReference(selectedReference));
    const [referenceDirty, setReferenceDirty] = useState(isReferenceDirty(selectedReference));

    const getShortId = function (refer: Reference | null, rmap: any | null): string {
        if (!refer) {
            return "";
        }
        if (!rmap) {
            rmap = getRefMap(refer);
        }
        var shortid = "";
        if (rmap.hasOwnProperty("UASID")) {
            shortid = rmap["UASID"];
            var idxSlash = shortid.lastIndexOf("/");
            if (idxSlash > 0) {
                shortid = shortid.substring(idxSlash + 1);
            }
        }
        return shortid;
    }
    const getFullId = function (refer: Reference | null, rmap: any | null): string {
        if (!refer) {
            return "";
        }
        if (!rmap) {
            rmap = getRefMap(refer);
        }
        var fullid = "";
        if (rmap.hasOwnProperty("UASID")) {
            fullid = rmap["UASID"];
        }
        return fullid;
    }
    const getLabel = function (refer: Reference | null, rmap: any | null): string {
        if (!refer) {
            return "";
        }
        if (!rmap) {
            rmap = getRefMap(refer);
        }
        var shortid = "";
        if (rmap.hasOwnProperty("UASID")) {
            shortid = rmap["UASID"];
            var idxSlash = shortid.lastIndexOf("/");
            if (idxSlash > 0) {
                shortid = shortid.substring(idxSlash + 1);
            } else if (shortid.split("-").length > 3 && rmap.hasOwnProperty("Name")) {
                // This is a guid so try and use the name
                shortid = rmap["Name"];
            }
        }
        if (shortid.length === 0) {
            var parts = refer.type.split('/');
            if (parts.length > 1) {
                shortid = parts[parts.length - 1];
            } else {
                shortid = parts[0];
            }
        }
        return shortid;
    }
    const [refId, setRefId] = useState(getFullId(selectedReference,null));

    const mergeWithChild = function (parent: StyledSelectableListItem | null) {
        if (parent == null || !parent.children) {
            return;
        }
        if (parent.children.length !== 1 ) {
            return;
        }
        var child: StyledSelectableListItem = parent.children[0];
        parent.label = child.label;
        parent.value = child.value;
        parent.disabled = child.disabled;
        parent.checked = child.checked;
        parent.className = child.className;
        parent.children = child.children;
    }
    const addPartId = function(partName: string, refMap: any, idparts: string[]) {
        if (refMap && refMap.hasOwnProperty(partName)) {
            idparts.push(refMap[partName]);
        }
    }
    const createReferenceKey = function (reference: Reference, refMap: any): string {
        var idparts : any[] = [];
        idparts.push(reference.type.replace("https", "http"));
        addPartId("Name", refMap, idparts);
        addPartId("Chapter", refMap, idparts);
        addPartId("Paragraph", refMap, idparts);
        idparts.push(getUasId(reference, refMap));
        return idparts.join("_");
    }

    //const [availableReferencesIncludingChanges, setAvailableReferencesIncludingChanges] = useState<Reference[]>([]);
    //         setAvailableReferencesIncludingChanges(availableRefs);

    const getAvailableReferencesIncludingChanges = function(refChangeList : Reference[], changedRefs : any) : Reference[] {
        if (!references) {
            return [];
        }
        var availableRefs : Reference[] = [];
        if (!changes) {
            availableRefs = references;
        } else {
            var sharedMap: any = {

            };
            var refKey = "";
            for (var originalRef of references) {
                sharedMap[createReferenceKey(originalRef, null)] = originalRef;
            }
            for (var changedRef of refChangeList) {
                var rmap = getRefMap(changedRef);
                refKey = createReferenceKey(changedRef, rmap);
                // deliberately overwrite with the changed or new references.
                sharedMap[refKey] = changedRef;
                changedRefs[getFullId(changedRef, rmap)] = changedRef;
            }
            for (var rkey of Object.keys(sharedMap).sort()) {
                availableRefs.push(sharedMap[rkey]);
            }
        }
        return availableRefs;
    }
    const [allRefsPlusChanges, setAllRefsPlusChanges] = useState<Reference[]>(getAvailableReferencesIncludingChanges(changes ? changes.referenceChanges : [], {}));

    const getReferencesList = function (currentRef: Reference | null): StyledSelectableListItem[] {
        var changedRefs: any = {};
        var availableRefs = getAvailableReferencesIncludingChanges(changes ? changes.referenceChanges : [], changedRefs);
        if (availableRefs.length === 0) {
            return [];
        }
        var alist: Array<StyledSelectableListItem> = [];
        if (availableRefs.length > 0) {
            var selectedId = (currentRef ? getShortId(currentRef, null) : "none");
            var currentTypeEntry: StyledSelectableListItem | null = null;
            var currentNameEntry: StyledSelectableListItem | null = null;
            var currentChapterEntry: StyledSelectableListItem | null = null;
            var currentParagraphEntry: StyledSelectableListItem | null = null;
            var rank = 0;
            var paragraphEntries : any[] = [];
            var chapterEntries : any[] = [];
            for (var refer of availableRefs) {
                var rmap = getRefMap(refer);
                var uasid = getShortId(refer, rmap);
                var bChecked = (selectedId === uasid);
                var itemClassName = "";
                var intermediateNodeClass = "";
                if (changedRefs.hasOwnProperty(uasid)) {
                    itemClassName = getReferenceStatusIconClass(refer);
                    if (itemClassName.length > 0) {
                        intermediateNodeClass = "bi bi-hexagon-half";
                    }
                }
                var listItem: StyledSelectableListItem = {
                    parentItem: null,
                    networkid: "none",
                    label: uasid,
                    value: rank + "",
                    disabled: false,
                    checked: bChecked,
                    className: itemClassName,
                    children: []
                }
                rank++;
                if (!currentTypeEntry || currentTypeEntry.label !== refer.type) {
                    currentTypeEntry = {
                        parentItem: null,
                        networkid: "none",
                        label: refer.type,
                        value: "-1",
                        disabled: true,
                        checked: false,
                        className: "",
                        children: []
                    };
                    alist.push(currentTypeEntry);
                    currentNameEntry = null;
                    currentChapterEntry = null;
                    currentParagraphEntry = null;
                }
                if (!refer.type.endsWith("/reference") ) {
                    currentTypeEntry.children.push(listItem);
                    if (intermediateNodeClass.length > 0) {
                        currentTypeEntry.className = intermediateNodeClass;
                    }
                    continue;
                }
                if (!rmap.hasOwnProperty("Name")) {
                    rmap["Name"] = "no name";
                }
                if (!rmap.hasOwnProperty("Chapter")) {
                    rmap["Chapter"] = "no chapter";
                }
                if (!currentNameEntry || currentNameEntry.label !== rmap["Name"]) {
                    currentNameEntry = {
                        parentItem: null,
                        networkid: "none",
                        label: rmap["Name"],
                        value: "-1",
                        disabled: true,
                        checked: false,
                        className: "",
                        children: []
                    };
                    currentTypeEntry.children.push(currentNameEntry);
                    currentChapterEntry = null;
                    currentParagraphEntry = null;
                }
                if (!currentChapterEntry || currentChapterEntry.label !== rmap["Chapter"]) {
 //                   mergeWithChild(currentChapterEntry);
                    currentChapterEntry = {
                        parentItem: null,
                        networkid: "none",
                        label: rmap["Chapter"],
                        value: "-1",
                        disabled: true,
                        checked: false,
                        className: "",
                        children: []
                    };
                    currentNameEntry.children.push(currentChapterEntry);
                    if (currentChapterEntry) {
                        chapterEntries.push(currentChapterEntry);
                    }
                    currentParagraphEntry = null;
                }
                if (!rmap.hasOwnProperty("Paragraph")) {
                    currentChapterEntry.children.push(listItem);
                    if (intermediateNodeClass.length > 0) {
                        currentTypeEntry.className = intermediateNodeClass;
                        currentNameEntry.className = intermediateNodeClass;
                        currentChapterEntry.className = intermediateNodeClass;
                    }
                    continue;
                }
                if (!currentParagraphEntry || currentParagraphEntry.label !== rmap["Paragraph"]) {
//                    mergeWithChild(currentParagraphEntry);
                    currentParagraphEntry = {
                        parentItem: null,
                        networkid: "none",
                        label: rmap["Paragraph"],
                        value: "-1",
                        disabled: true,
                        checked: false,
                        className: "",
                        children: []
                    };
                    currentChapterEntry.children.push(currentParagraphEntry);
                    paragraphEntries.push(currentParagraphEntry);
                }
                currentParagraphEntry.children.push(listItem);
                if (intermediateNodeClass.length > 0) {
                    currentTypeEntry.className = intermediateNodeClass;
                    currentNameEntry.className = intermediateNodeClass;
                    currentChapterEntry.className = intermediateNodeClass;
                    currentParagraphEntry.className = intermediateNodeClass;
                }
            }
            for (var pentry of paragraphEntries) {
                mergeWithChild(pentry);
            }
            for (var pentry of chapterEntries) {
                mergeWithChild(pentry);
            }
        }
        return alist;
    }
    const [referencesList, setReferencesList] = useState<StyledSelectableListItem[]>(getReferencesList(selectedReference));

    const getAssignedReferencesList = function (): Array<StyledSelectableListItem> {
        var alist: Array<StyledSelectableListItem> = [];
        if (!versionedConcept || !versionedConcept.concept ) {
            return alist;
        }
        var assignedRefs = getAssignedReferences(versionedConcept);
        if (!assignedRefs) {
            return alist;
        }
        var currentRef = selectedReference;
        if (assignedRefs.length > 0) {
            var selectedId = (currentRef ? getFullId(currentRef, null) : "none");
            var rank = 0;
            for (var refer of assignedRefs) {
                var rmap = getRefMap(refer);
                var uasid = getFullId(refer, rmap);
                var bChecked = (selectedId === uasid);

                var listItem: StyledSelectableListItem = {
                    parentItem: null,
                    networkid: "none",
                    label: getLabel(refer, rmap),
                    value: rank + "",
                    disabled: false,
                    checked: bChecked,
                    className: getReferenceStatusIconClass(refer),
                    children: []
                }
                alist.push(listItem);
                rank++;
            }
        }
        return alist;
    }
    const getAssignedReferenceIdsList = function () {
        var alist: Array<string> = [];
        for (var selItem of getAssignedReferencesList()) {
            alist.push(selItem.label);
        }
        return alist;
    }
    const [assignedReferencesList, setAssignedReferencesList] = useState<StyledSelectableListItem[]>(getAssignedReferencesList());
 
    const ddhandleReferenceChange = function (currentNode: any, selectedNodes: any) {
        if (selectedNodes.length === 0) {
            dispatch(clearSelectedReference);
            return;
        }
        var refrank = parseInt(selectedNodes[0].value);
        var selref : any | null = null;
        var changedRefs: any = {};
        var reflist = getAvailableReferencesIncludingChanges(changes ? changes.referenceChanges : [], changedRefs);
        if (refrank >= 0 && reflist.length > refrank) {
            selref = reflist[refrank];
        }
        if (selref) {
            var rmap: any = getRefMap(selref);
            var rid: string = getFullId(selref, rmap);
            setRefId(rid);
            setReferenceDirty(isReferenceDirty(selref));
            switchReference(selref);
            if (rid.length > 0) {
                var req: ReferenceAssignmentsRequest = {
                    taxonomyType: versionType,
                    taxonomyYear: versionYear,
                    referenceId: getShortId(selref, rmap)
                }
                dispatch(loadAllReferenceAssignments(req));
            }
            else {
                dispatch(clearSelectedReferenceAssignments());
            }
//            setWhereUsed(refConcepts)
        } else {
            setRefId("");
            dispatch(clearSelectedReference());
        }
    }

    const ddhandleAssignedReferenceChange = function (currentNode: any, selectedNodes: any) {
        if (selectedNodes.length === 0) {
            dispatch(clearSelectedReference);
            return;
        }
        if (!currentRefAssignments) {
            return;
        }
        var refrank = parseInt(selectedNodes[0].value);
        var selref : any  | null = null;
        if (refrank >= 0 && currentRefAssignments.length > refrank) {
            selref = currentRefAssignments[refrank];
        }
        if (selref) {
            var rmap: any = getRefMap(selref);
            var rid: string = getFullId(selref, rmap);
            setRefId(rid);
            setReferenceDirty(isReferenceDirty(selref))
            switchReference(selref);
            if (rid.length > 0) {
                // Note we use the short ID because the REST call model does not like the full ids
                var req: ReferenceAssignmentsRequest = {
                    taxonomyType: versionType,
                    taxonomyYear: versionYear,
                    referenceId: getShortId(selref, rmap)
                }
                dispatch(loadAllReferenceAssignments(req));

            } else {
                dispatch(clearSelectedReferenceAssignments());
            }
//            setWhereUsed(refConcepts)
        } else {
            setRefId("");
            dispatch(clearSelectedReference());
        }
    }
/*
    useEffect(() => {
        var vRef: VersionRef = getReferenceForVersion(versionInfo, -1);
        setVersionType(vRef.type);
        setVersionYear(vRef.year);
        setAllPartDefinitions(collectAllPartDefinitions());
        setTableData([]);
        dispatch(resetTaxonomyConceptState());
        dispatch(clearSelectedReference());
        setAllRefsPlusChanges(getAvailableReferencesIncludingChanges(changes ? changes.referenceChanges : [], {}));
        setReferencesList(getReferencesList(null));
        setAssignedReferencesList(getAssignedReferencesList());
        dispatch(loadAllTaxonomyReferences(vRef));
        setRefIsAssignable(false);
        setRefIsUnAssignable(false);
    }, [versionInfo]);
*/
    useEffect(() => {
        setCurrentRefAssignments(getAssignedReferences(versionedConcept));
        setTableData([]);
        dispatch(clearSelectedReference());
        setReferencesList(getReferencesList(null));
        setAssignedReferencesList(getAssignedReferencesList());
        setRefIsAssignable(false);
        setRefIsUnAssignable(false);
    }, [versionedConcept]);

    useEffect(() => {
        var current: Reference | null = selectedReference;
        if (current == null) {
            setReferenceEditable(false);
            setReferenceDirty(false);
            setCurrentRef(null);
            setRefIsAssignable(false);
            setRefIsUnAssignable(false);
            setRefId("");
            setRefUasId("");
            setRefDate("");
            setRefName("");
            setRefArticle("");
            setTableData([]);
            setReferenceStatusIcon("");
            setReferenceEditStatusIcon("");
            setTablePartMap({});
        } else {
            setReferenceEditable(canEditCurrentReference(current));
            setReferenceDirty(isReferenceDirty(current));
            setCurrentRef(current);
            setRefIsAssignable(canBeAssigned());
            setRefIsUnAssignable(canBeUnAssigned());
            setRefId(getFullId(current, null))
            setRefUasId(getFormattedPartValue(current, "UASID"));
            setRefDate(getFormattedPartValue(current, "IssueDate"));
            setRefName(getFormattedPartValue(current, "Name"));
            if (referenceHasPart(current, "Article")) {
                setRefArticle(getFormattedPartValue(current, "Article"))
            }
            var td: RefTableData[] = getTableData(current);
            setTableData(td);
            setReferenceStatusIcon(getReferenceStatusIconClass(current));
            setReferenceEditStatusIcon(getReferenceEditStatusIconClass(current));
            updateTablePartMap(current);
        }
        setReferencesList(getReferencesList(current));
        setAssignedReferencesList(getAssignedReferencesList())
    }, [selectedReference]);

    useEffect(() => {
        var crefList = changes ? changes.referenceChanges : [];
//        console.log("use effect", changes ? changes.referenceChanges : null );
        setAllPartDefinitions(collectAllPartDefinitions());
        setAllRefsPlusChanges(getAvailableReferencesIncludingChanges(crefList,{}));
        setReferencesList(getReferencesList(selectedReference));
        setCurrentRefAssignments(getAssignedReferences(versionedConcept));
        setAssignedReferencesList(getAssignedReferencesList());
    }, [changes]);

    useEffect(() => {
        var current: Reference | null = currentEditReference;
        if (current) {
            var td: RefTableData[] = getTableData(current);
            setTableData(td);
            updateTablePartMap(current);
        } else {
            setTablePartMap({});
        }
    }, [currentEditReference]);



    // Cell Handler
    const handleCellEdited = function (cell: any) {
        var valueMap: any = {};
        cell.getTable().getData().map((rdata: any) => {
            valueMap[rdata.part] = rdata.value;
        });
        var pmNew: any = {};
        for (var pkey of Object.keys(tablePartMap)) {
            var prevPart: any = { ...tablePartMap[pkey] };

            if (valueMap.hasOwnProperty(pkey)) {
                prevPart.value = valueMap[pkey];
            }
            pmNew[pkey] = prevPart;
        }
        setTablePartMap(pmNew);
    }

    const validationFailed = function (cell: any, value: string, parameters: any) {
        console.log("validation cell", cell.row.getData());
//        setValidationError(validationErrorDetails);
    }


    var newGridOptions = {
        cellEdited: (cell: any) => handleCellEdited(cell),
        validationFailed: (cell: any, value: string, parameters: any) => validationFailed(cell, value, parameters),
        validationMode: "highlight",
    };

    const handleClosePartModal = function () {
        setModalPartIsOpen(false);
        dispatch(clearEditReference);
    }

    const handleCloseReferenceModal = function (newRef: Reference) {
        setModalReferenceIsOpen(false);
        // note the use of '==' here, the abstract equality operator that catches both null and undefined
        if (newRef == null) {
            dispatch(clearSelectedReference);
            return;
        }
        switchReference(newRef);
    }


    // handle the logic to add a new account 
    const addPartTitleFormatter = function (cell : any, formatterParams : any, onRendered : any) {

        //build dropdown
        var button = document.createElement("div");

        button.innerHTML = "<i class='bi bi-plus-circle' title='Add new Part...'></i>  Part";

        button.onclick = function () {
            // add code here

            if (selectedReference) {
                var editRef: Reference | null = createEditedReference(selectedReference);
                if (editRef) {
                    dispatch(setEditReference(editRef));
                    setModalPartIsOpen(true);
                }
            }
        }

        return button;
    }

    // Reference table formatting
    const genericFormatter = function (cell: any) {

        var row = cell.getRow();
        var data = cell.getData();
        var dataType = data.datatype;
        var value = utilityHelper.getNumber(cell.getValue());

        var formatted = utilityHelper.Formatter(cell.getValue(), dataType);

        return formatted;

    }

    const hasInvalidIdChars = function (candidate: string): boolean {
        for (var c = 0; c < candidate.length; c++) {
            if (" ,;\"'\\(){}[]".indexOf(candidate[c]) >= 0) {
                console.log("invalid char: ", candidate[c], c);
                return true;
            }
        }
        return false;
    }
    const isUniqueId = function (candidate: string): boolean {
        for (var r of getAvailableReferencesIncludingChanges(changes ? changes.referenceChanges : [],{})) {
            if (getUasId(r, null) === candidate) {
                return false;
            }
        }
        return true;
    }
    const isValid = function (field: string) {
        var result: boolean = true;
        if (!selectedReference) {
            return true;
        }
        if (!isDirtyReference(selectedReference)) {
            return true;
        }
        switch (field) {
            case "UASID":
                if (!refId || refId.length === 0) {
                    result = false;
                } else if (hasInvalidIdChars(refId)) {
                    result = false;
                }
                break;
            case "Name":
                if (!refName || refName.length === 0) {
                    result = false;
                }
                break;
            case "documentation":
//                if (!documentation || documentation.length === 0) {
//                    result = false;
//                }
                break;
        }
        return result;
    }

    const setPartValue = function (reference: Reference | null, partName: string, partValue: string) : string {
        if (!reference) {
            return partValue;
        }
        var dtype : string = "";
        if (partName === "xIssueDate") {
            dtype = "XBRLI_DATEITEMTYPE";
        } else {
            var pd: PartDefinition = getPartDefinition(partName);
            if (!pd) {
                return partValue;
            }
            dtype = pd.datatype;
        }
        var convertedValue = utilityHelper.convertText(partValue, dtype);
        var pv: PartValue = {
            partName: partName,
            partValue: convertedValue
        }
        dispatch(updateSelectedReferencePart(pv));
        setTaxonomyDirty();
        return convertedValue;
    }

    const getFormattedPartValue = function (reference: Reference | null, partName: string): string {
        if (!reference) {
            return "";
        }
        var pvalue : string = getPartValue(reference, partName);
        if (!pvalue) {
            return "";
        }
        if (partName === "XIssueDate") {
            return utilityHelper.Formatter(pvalue, "XBRLI_DATEITEMTYPE");
        }
        var pd = getPartDefinition(partName);
        if (!pd) {
            return "error missing part definition: " + partName;
        }
        return utilityHelper.Formatter(pvalue, pd.datatype);
    }
    const [refUasId, setRefUasId] = useState<string>(getFormattedPartValue(selectedReference, "UASID"));
    const [refDate, setRefDate] = useState<string>(getFormattedPartValue(selectedReference, "IssueDate"));
    const [refName, setRefName] = useState<string>(getFormattedPartValue(selectedReference, "Name"));
    const updateRefArticle = function (art: string) {
        setRefArticle(art);
        setReferenceDirty(true);
    }

    const validateValueUsingType = function (datatype: string, field : string, newValue: string ) {
        var error: ErrorGrid | null = null;
        var value: string = "";
        if (!(utilityHelper.isNullOrEmpty(newValue))) {
            value = newValue;

        }


        /*1 - check if field is required */
        if (value === null || value === "") {
            error = { field: field, type: "required", message: field + " is required." }
            return { result: false, error };
        }

    /* 2 - Validate for duplicates rows  */
        switch (datatype) {
            case "XBRLI_DATEITEMTYPE":
                var re = /(\d{4}-\d{2}-\d{2})/
                var found = value.match(re);

               if (!found) {
                   error = { field: field, type: "format", message: field + " contains no valid ISO date format (yyyy-mm-dd)." }
                   return { result: false, error };
                }
                if (found[0] !== value ) {
                    error = { field: field, type: "format", message: field + " contains additional characters as well as an ISO date." }
                    return { result: false, error };
                }
                break;
        }
        


        /* ******************************************** */
        return { result: true, error };
    }
    const truncatedTypeReference = function (stype: string): string {
        return stype.replace("https://www.apps.fldfs.com/LocalGov/","{fldfs}")
    }
    const validators = function (cell: any, newValue: string, parameters: any) {
        //cell - the cell component for the edited cell
        //value - the new input value of the cell
        //parameters - the parameters passed in with the validator
        var error: ErrorGrid | null = null;
        var row = cell.getRow();
        var fields = "value";
        var rowData: any = row.getData();
        var fieldMap: any = {};
        for (var s of fields.split("|")) {
            if (rowData.hasOwnProperty(s)) {
                var sval = rowData[s];
                if (sval && sval !== undefined) {
                    fieldMap[s] = rowData[s];
                }
            }
        }
        var value: string = "";
        if (!(utilityHelper.isNullOrEmpty(newValue))) {
            value = newValue;

        }

        /*1 - check if field is required */
        if (value === null || value === "") {
            error = { field: cell.getField(), type: "required", message: cell.getField() + " is required." }
            return { result: false, error };
        }

        /* 2 - Validate for duplicates rows  */
        switch (rowData.datatype) {
            case "XBRLI_DATEITEMTYPE":
                var re = /(\d{4}-\d{2}-\d{2})/
                var found = value.match(re);

                if (!found) {
                    error = { field: cell.getField(), type: "format", message: cell.getField() + " contains no valid ISO date format (yyyy-mm-dd)." }
                    return { result: false, error };
                }
                if (found[0] !== value) {
                    error = { field: cell.getField(), type: "format", message: cell.getField() + " contains additional characters as well as an ISO date." }
                    return { result: false, error };
                }
                break;
        }


        /* 2 - Validate for duplicates rows  */
        var rowDimkey = utilityHelper.getRowDimensionsKey(rowData, cell.getField(), newValue);
        if (utilityHelper.checkForRowDuplicates(cell.getTable(), rowData.key, rowDimkey)) {
            error = { field: "", type: "duplicate", message: rowDimkey + " is duplicated." }
            return { result: false, error };
        }


        /* 3. Rates must be positive numbers  */
        var field: string = cell.getField();

        if (fields.indexOf(field) > 0) {
            if (utilityHelper.getNumber(value) < 0) {
                error = { field: field, type: "negative", message: field + " cannot be negative." }
                return { result: false, error };
            }
        }


        /* 4 - validate ranges  */
        var fields = "MaximumMember|MinimumMember";

        if (fields.indexOf(field) >= 0) {

            var min = row.getData().MinimumMember;
            var max = row.getData().MaximumMember;

            if (!(utilityHelper.isNullOrEmpty(min) || utilityHelper.isNullOrEmpty(max))) {
                if (utilityHelper.getNumber(min) > utilityHelper.getNumber(max)) {
                    error = { field: "MinimumMember", type: "rates", message: "Minimum cannot be greater than Maximum." }
                    return { result: false, error };
                }
            }


        }


        /* ******************************************** */
        return { result: true, error };
    }

    const customValidator = function (cell : any, value : string, parameters : any) {

        const { result, error } = validators(cell, value, parameters);
        return result;
    }


    var defaultColumns = [
        { title: "Part", field: "part", width: 150, titleFormatter: addPartTitleFormatter },
        {
            title: "Value", field: "value", editor: "textarea", formatter: "textarea", validator: [{ type: customValidator }], cellEdited: handleCellEdited,
        }
    ];
    const secondElementStyle = {
        marginTop: '4pt',
        marginLeft: '1em'
    }
    const editButtonStyle = {
        marginTop: '4pt',
        marginLeft: '1em',
        marginBottom: '4pt'
    }
    const leftMarginStyle = {
        marginLeft: '1em'
    }
    const selRefStyle = {
        marginTop: '8pt',
        marginLeft: '1em'
    }
    const rightAlignStyle = {
        align: 'right'
    }
    const flexRow = {
        display: 'flex'
    }
    const flexRightChild = {
        marginLeft: 'auto',
        marginRight: '1em'
    }
    const referenceStatusIconStyle = {
        fontSize: '2rem',
        fontWeight: 400,
        padding: '0.375rem 0.75rem',
        color: 'red'
    }

    const handleNewSharedClick = () => {
        setModalReferenceIsOpen(true);
    }

    const handleCopyOrEdit = (reference: Reference, bGenerateId: boolean) => {
        if (!reference) {
            return;
        }
        var copyParts: Part[] = [];
        for (var p of reference.parts) {
            var newPart: Part = { ...p };
            switch (p.definition.name) {
                case "UASID":
                    if (bGenerateId) {
                        newPart.value = utilityHelper.generateGuid();
                    }
                    break;
                case "IssueDate":
                    newPart.value = new Date().toISOString().split('T')[0];
                    break;
            }
            if (newPart) {
                copyParts.push(newPart);
            }
        }
        var newRef: Reference = {
            type: reference.type,
            parts: copyParts,
            dirty: true,
            new: bGenerateId
        };
        setReferenceDirty(true);
        setReferenceEditStatusIcon(getReferenceEditStatusIconClass(newRef));
        switchReference(newRef);
    }

    const handleCopyClick = () => {
        if (selectedReference) {
            handleCopyOrEdit(selectedReference, true);
        }
    }

    const handleEditClick = () => {
        if (selectedReference) {
            handleCopyOrEdit(selectedReference, false);
        }
    }

    const addPart = function(partName : string, partValue : string, newParts : Part[]) {
        var pd : PartDefinition | null = getPartDefinition(partName);
        if( !pd ) {
            return;
        }
        newParts.push( {
            definition: {...pd},
            value: partValue
        });
    }
    const createEditedReferenceValidationErrors = function (r: Reference | null): ErrorGrid[] {
        var errors: ErrorGrid[] = [];
        if (!r) {
            return [];
        }
        if (!tabulatorRef) { 
            return [];
        }
        if (!tabulatorRef.current) {
            return [];
        }    
        var table = tabulatorRef.current.table;
        if (!table) {
            return [];
        }
        for (var row of table.getData("active")) {
            if (row.datatype === "none") {
                // This is a spacer row at the end to deal with a horizontaly scroll bar that appears when you edit.
                continue;
            }
            var errReturn = validateValueUsingType(row.datatype, row.part, row.value);
            if (errReturn && !errReturn.result && errReturn.error) {
                errors.push(errReturn.error);
            }
        }
        return errors;
    }

    const createEditedReference = function(r: Reference | null) : Reference | null {
        if (!r) {
            return null;
        }
        var newParts: Part[] = [];
        if (isShareable(r)) {
            addPart("UASID", refId, newParts);
            addPart("IssueDate", refDate, newParts);
            if (refName && refName.length > 0) {
                addPart("Name", refName, newParts);
            }
        } else {
            addPart("IssueDate", refDate, newParts);
        }
        if (!tabulatorRef.current || !tabulatorRef.current.table) {

            for (var pkey of Object.keys(tablePartMap)) {
                newParts.push(tablePartMap[pkey]);
            }
        } else {
            if (tabulatorRef.current && tabulatorRef.current.table) {
                var table = tabulatorRef.current.table;
                for (var row of table.getData("active")) {
                    if (row.datatype === "none") {
                        // This is a spacer row at the end to deal with a horizontaly scroll bar that appears when you edit.
                        continue;
                    }
                    addPart(row.part, row.value, newParts);
                }
            }
        }
        if (referenceHasPart(r, "Article")) {
            addPart("Article", refArticle, newParts);
        }
        var newRef: Reference = { ...r, parts: newParts, dirty: false }
        return newRef        
    }

    const handleCancelClick = () => {
        switchToLastSavedReference(selectedReference);
    }

    const handleSaveClick = () => {
        var validationErrors: ErrorGrid[] = createEditedReferenceValidationErrors(selectedReference);
        if (validationErrors.length > 0) {

            alert(validationErrors.map(e => e.message).join("\n") + "\n\nPlease correct these Validation errors before saving.");
            return;
        }
        var newRef: Reference | null = createEditedReference(selectedReference);
        if (!newRef) {
            return;
        }
        dispatch(setSelectedReference(newRef));
//        dispatch(clearSelectedReferenceDirty());
        dispatch(addReferenceChange(newRef));
        setTaxonomyDirty();
        if (versionedConcept && versionedConcept.concept && (newRef.type === enums.referenceTypes.private || newRef.type === enums.referenceTypes.info) ) {
            // Only automatically assign for private references. Shared references need to be explicitly assigned
            var newAssignment: ReferenceAssignmentChange = {
                reference: newRef,
                concept: versionedConcept.concept,
                remove: false
            }
            dispatch(addReferenceAssignmentChange(newAssignment));
        }
        setReferencesList(getReferencesList(selectedReference));
    }

    const handleAssign = (bRemove : boolean) => {
        var r: Reference | null = selectedReference;
        if (!r) {
            return;
        }
        dispatch(clearSelectedReferenceDirty());
        if (versionedConcept && versionedConcept.concept) {
            var newAssignment: ReferenceAssignmentChange = {
                reference: r,
                concept: versionedConcept.concept,
                remove: bRemove
            }
            dispatch(addReferenceAssignmentChange(newAssignment));
            setTaxonomyDirty();
        }
    }

    const handleAssignClick = () => {
        handleAssign(false);
    }

    const handleUnAssignClick = () => {
        handleAssign(true);
    }


    React.useEffect(() => {
        if (referenceDirty) {
            tabulatorRef.current = new Tabulator('#conceptReference', {
                data: tableData,
                columns: defaultColumns,
                layout: "fitColumns",
                validationFailed: (cell: any, value: string, parameters: any) => validationFailed(cell, value, parameters),
                validationMode: "highlight",
                cellEdited: (cell: any) => handleCellEdited(cell)
            });
        } else {
            tabulatorRef.current = null;
        }
    }, [,tableData])


    return (
        <React.Fragment>
            <Row>
                <Col sm="9">
                    <Row>
                        <Col sm="6">
                            <Row>
                                <h5 style={secondElementStyle}>
                                    <React.Fragment>
                                        <DropdownTreeSelect
                                            id="referencePicker"
                                            data={referencesList}
                                            mode="radioSelect" keepTreeOnSearch={true} keepChildrenOnSearch={true}
                                            onChange={ddhandleReferenceChange}
                                            texts={{ placeholder: "Choose Shared Reference..." }} />
                                    </React.Fragment>
                                </h5>
                            </Row>
                            <Row>
                                <Button style={editButtonStyle} type="button" className="taxedit" disabled={referenceDirty} onClick={handleNewSharedClick}>New</Button>
                                <Button style={editButtonStyle} type="button" className="taxedit" disabled={referenceDirty || !referenceEditable} onClick={handleCopyClick}>Copy</Button>
                                <Button style={editButtonStyle} type="button" className="taxedit" disabled={referenceDirty || !referenceEditable} onClick={handleEditClick}>Edit</Button>
                                {referenceStatusIcon.length > 0
                                    ? <div style={referenceStatusIconStyle} ><i className={referenceStatusIcon} ></i></div>
                                    : <div></div>
                                }
                                {referenceEditStatusIcon.length > 0
                                    ? <div style={referenceStatusIconStyle} ><i className={referenceEditStatusIcon} ></i></div>
                                    : <div></div>
                                }
                                <div style={editButtonStyle} ><h5>{refId}</h5></div>
                            </Row>
                            <Row>
                                <Col md="11" >
                                    <FormGroup>
                                        <Row>
                                            <Col className="text-md-right" >
                                                <Label><b>Reference Type:</b></Label>
                                            </Col>
                                            <Col md="9">
                                                <Label>{selectedReference ? truncatedTypeReference(selectedReference.type) : ""}</Label>
                                            </Col>
                                        </Row>
                                        {isShareable(selectedReference) &&
                                            <Row>
                                                <Col className="text-md-right" >
                                                    <Label><b>ID:</b></Label>
                                                </Col>
                                                <Col md="9">
                                                    <Input type="text" value={refId}
                                                        onChange={(e) => setRefId(e.currentTarget.value)}
                                                        disabled={!referenceDirty}
                                                        valid={isValid("UASID")}
                                                        invalid={(!isValid("UASID"))} />
                                                    <FormFeedback>UASID is required, and must be unique. No spaces or punctuation allowed.</FormFeedback>
                                                </Col>
                                            </Row>
                                        }
                                        {isShareable(selectedReference) &&
                                            <Row>
                                                <Col className="text-md-right" >
                                                    <Label><b>Name:</b></Label>
                                                </Col>
                                                <Col md="9">
                                                    <Input type="text" value={refName}
                                                        onChange={(e) => setRefName(e.currentTarget.value)}
                                                        disabled={!referenceDirty}
                                                        valid={isValid("Name")}
                                                        invalid={(!isValid("Name"))} />
                                                    <FormFeedback>Name is required.</FormFeedback>
                                                </Col>
                                            </Row>
                                        }
                                        <Row>
                                            <Col className="text-md-right" >
                                                <Label><b>Effective Date:</b></Label>
                                            </Col>
                                            <Col md="9">
                                                <Input type="date" value={refDate}
                                                    onChange={(e) => setRefDate(e.currentTarget.value)}
                                                    disabled={!referenceDirty}
                                                    valid={isValid("IssueDate")}
                                                    invalid={(!isValid("IssueDate"))} />
                                                <FormFeedback>Effective date is required.</FormFeedback>
                                            </Col>
                                        </Row>

                                    </FormGroup>
                                </Col>
                                <Col sm="1" >
                                    <Button className="mr-3 btn btn-success" disabled={!refIsAssignable}
                                        onClick={handleAssignClick}
                                    ><i className="bi bi-arrow-right-circle"></i>

                                    </Button>
                                    <Button style={{ marginTop: "1em" }} className="mr-3 btn btn-success" disabled={!refIsUnAssignable}
                                        onClick={handleUnAssignClick}
                                    ><i className="bi bi-arrow-left-circle"></i>

                                    </Button>

                                </Col>
                            </Row>
                        </Col>
                        <Col sm="3">
                            <div style={secondElementStyle} >
                                <div><h5>Currently assigned</h5></div>
                                {assignedReferencesList.length > 0
                                    ?
                                    <div>
                                        <React.Fragment>
                                            <DropdownTreeSelect
                                                id="referenceAssignment"
                                                data={assignedReferencesList}
                                                mode="radioSelect" keepTreeOnSearch={true} keepChildrenOnSearch={true}
                                                showDropdown="initial"
                                                inlineSearchInput={true}
                                                onChange={ddhandleAssignedReferenceChange}
                                                texts={{ placeholder: " " }}
                                            />
                                        </React.Fragment>
                                    </div>
                                    :
                                    <div></div>
                                }
                            </div>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            {referenceDirty
                                ?
                                <Row>
                                    <Col>
                                        <Row>
                                            <div style={flexRightChild} >
                                                <Button type="button" style={leftMarginStyle} className="taxedit" disabled={!referenceDirty} onClick={handleCancelClick}>Cancel</Button>
                                                <Button type="button" style={leftMarginStyle} className="taxedit" disabled={!referenceDirty} onClick={handleSaveClick}>Save</Button>
                                            </div>
                                        </Row>
                                        <Row>
                                            <div id="conceptReference" style={{ width: "100%" }} ref={ele => { tabulatorRef.current = ele }} />
                                        </Row>
                                    </Col>
                                </Row>
                                :
                                <div>
                                    <Table className="table" >
                                        <thead className="scrollable-table-header thead-dark">
                                            <tr>
                                                <th style={{ width: "10%" }}>Part</th>
                                                <th>Value</th>
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {tableData.map((option, idx) => (
                                                <tr>
                                                    <td>{option.part}</td>
                                                    <td>{option.value.indexOf("\n") < 0
                                                        ?
                                                        <span>{option.value}</span>
                                                        :
                                                        <div>
                                                            {option.value.split("\n").map((line) => (<p style={{ marginBottom: "0" }}>{line}</p>))}
                                                        </div>

                                                    }</td>
                                                </tr>
                                            ))}

                                        </tbody>
                                    </Table>
                                </div>
                            }
                            {!referenceHasPart(selectedReference, "Article") ? <></> :
                                <div>
                                    <Label>Article</Label>
                                    <WYSIWYGEditor
                                        content={refArticle}
                                        setContent={setRefArticle}
                                        readOnly={!selectedReference || !referenceDirty}
                                    />
                                </div>
                            }
                        </Col>
                    </Row>
                </Col>
                <Col sm="3">
                    <Row>
                        <Col>
                            <div><h5>Used by</h5></div>
                            <ul>
                                {refConcepts.map((detail) => {
                                    return <li key={detail.code}>{detail.name}</li>
                                }
                                )}
                            </ul>
                        </Col>
                    </Row>
                </Col>
            </Row>
            {selectedReference && (<SelectNewPartModal
                isOpen={modalPartIsOpen}
                onCloseModal={handleClosePartModal} />)}
            <SelectNewReferenceTypeModal
                availableReferences={allRefsPlusChanges}
                isOpen={modalReferenceIsOpen}
                onCloseModal={handleCloseReferenceModal} />


        </React.Fragment>
    );
}
export default ConceptReferenceEditor;